import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { inject, injectable } from "inversify";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { fromPromise } from "mobx-utils";
import type { IOrganizationStore } from "../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../services/IOrganizationStore";
import { parseOrReturnError } from "../../services/utils";
import { deleteJSON, getJSON, postJSON, putJSON } from "@whatslly/commons/apiUtils";
import { DLPPolicy } from "@whatslly/types/dlp/DLPPolicy";
import { modalService } from "@whatslly/ui-components/modals/ModalService";
import { ConfirmModal } from "@whatslly/ui-components/modals/ConfirmModal";
import { TFunction } from "i18next";
import { DLPRule } from "@whatslly/types/dlp/DLPRule";
import { DLPRuleModal } from "./DLPRuleModal";
import type { IDLPStore } from "./IDLPStore";
import { MessageModal } from "@whatslly/ui-components/modals/MessageModal";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { ImportConfigModal } from "../SuperAdminOrgConfigs/ImportConfigModal";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import i18n from "../../i18n/config";
import { GetDlpPoliciesResponse } from "@whatslly/types/integrations/DlpPoliciesRequest";
import { GetDlpRulesResponse } from "@whatslly/types/integrations/DlpRulesRequest";
import { DestinationsStore } from "../Destinations/DestinationsStore";
import { OrgConfigsStore } from "../../services/OrgConfigsStore";
import { UrlStore } from "../../services/urlStore";

dayjs.extend(localizedFormat);

@injectable()
export class DLPStore implements IDLPStore {
  private forceUpdate = 0;
  policiesSearchTerm = "";
  rulesSearchTerm = "";

  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(DestinationsStore) private readonly destinationsStore: DestinationsStore,
    @inject(OrgConfigsStore) private readonly orgConfigsStore: OrgConfigsStore,
    @inject(UrlStore) private readonly urlStore: UrlStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable<DLPStore, "forceUpdate">(this, {
      forceUpdate: observable,
      policiesSearchTerm: observable,
      rulesSearchTerm: observable,
      policies: computed,
      rules: computed,
      allowDlpAI: computed,
      isSidecarEnabled: computed,
      isDlpEnabled: computed,
      setPoliciesSearchTerm: action,
      setRulesSearchTerm: action,
      createPolicy: action.bound,
      createRule: action.bound,
    });
  }

  get policies() {
    this.forceUpdate;
    return fromPromise(this.getDlpPolicies());
  }

  get rules() {
    this.forceUpdate;
    return fromPromise(this.getDlpRules());
  }

  get allowDlpAI() {
    this.forceUpdate;
    return this.orgConfigsStore.getObservableConfig("allow_dlp_ai");
  }

  get isSidecarEnabled() {
    this.forceUpdate;
    return this.orgConfigsStore.getObservableConfig("sidecars_enabled");
  }

  get isDlpEnabled() {
    this.forceUpdate;
    return (
      this.destinationsStore.destinationsShallow.state === "fulfilled" &&
      this.destinationsStore.destinationsShallow.value.some((t) => t.type === "tuvisDLP")
    );
  }

  private async getDlpRules() {
    const res = await getJSON({
      urlSuffix: `/integrations/dlp/rules?orgId=${await this.orgStore.orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
    }).then((r) => parseOrReturnError<GetDlpRulesResponse>(r, this.logger));
    return res.success ? res.rules : [];
  }

  private async getDlpPolicies() {
    const res = await getJSON({
      urlSuffix: `/integrations/dlp/policies?orgId=${await this.orgStore.orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
    }).then((r) => parseOrReturnError<GetDlpPoliciesResponse>(r, this.logger));
    return res.success ? res.policies : [];
  }

  async getPolicyById(policyId: string) {
    const policies = await this.getDlpPolicies();
    return policies.find((policy) => policy.id === policyId);
  }

  private reload() {
    runInAction(() => this.forceUpdate++);
  }

  private getPolicyBuilderUrl(policyId?: string) {
    const search = this.urlStore.searchParams?.toString();
    const basePath = policyId ? `/dlp/policies/${policyId}` : "/dlp/policies/new";
    return search ? `${basePath}?${search}` : basePath;
  }

  setPoliciesSearchTerm(term: string) {
    this.policiesSearchTerm = term;
  }

  setRulesSearchTerm(term: string) {
    this.rulesSearchTerm = term;
  }

  async createPolicy() {
    this.urlStore.navigate(this.getPolicyBuilderUrl());
  }

  async savePolicy(policy: DLPPolicy, isEditMode = false) {
    if (isEditMode) {
      await this.updatePolicy(policy);
      return;
    }

    await postJSON({
      urlSuffix: `/integrations/dlp/policies?orgId=${await this.orgStore.orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ policy }),
    });
    this.reload();
  }

  async deletePolicy(t: TFunction, policy?: DLPPolicy) {
    if (!policy) {
      return;
    }
    const res = await modalService.openModal(ConfirmModal, {
      title: t("dlp_page.delete_policy"),
      body: t("dlp_page.delete_policy_modal_message"),
    }).promise;
    if (!res?.result) {
      return;
    }
    await deleteJSON({
      urlSuffix: `/integrations/dlp/policies?orgId=${await this.orgStore.orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ policyIds: [policy.id] }),
    });
    this.reload();
  }

  private async updatePolicy(policy: DLPPolicy) {
    await putJSON({
      urlSuffix: `/integrations/dlp/policies/${policy.id}?orgId=${await this.orgStore.orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ policy }),
    });
    this.reload();
  }

  async editPolicy(policy?: DLPPolicy) {
    if (!policy) {
      return;
    }

    this.urlStore.navigate(this.getPolicyBuilderUrl(policy.id));
  }

  async togglePolicyStatus(policy: DLPPolicy) {
    await this.updatePolicy({ ...policy, enabled: !policy.enabled });
  }

  async deleteRule(t: TFunction, rule?: DLPRule) {
    if (!rule) {
      return;
    }
    const policies = await this.policies;
    const used = policies.filter((p) =>
      p.conditions.some((c) => {
        if (c.type !== "rule") return false;
        return Array.isArray(c.id) ? c.id.includes(rule.id) : c.id === rule.id;
      })
    );
    if (used.length > 0) {
      await modalService.openModal(MessageModal, {
        title: t("dlp_page.rule_in_use"),
        children: t("dlp_page.rule_in_use_description", { policies: used.map((p) => p.name).join(", ") }),
        okTestId: "ok_button",
      }).promise;
      return;
    }
    const res = await modalService.openModal(ConfirmModal, {
      title: t("dlp_page.delete_rule"),
      body: t("dlp_page.delete_rule_modal_message"),
    }).promise;
    if (!res?.result) {
      return;
    }
    await deleteJSON({
      urlSuffix: `/integrations/dlp/rules?orgId=${await this.orgStore.orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ ruleIds: [rule.id] }),
    });
    this.reload();
  }

  async createRule() {
    const rule = await modalService.openModal(DLPRuleModal).promise;
    if (!rule) return;
    await postJSON({
      urlSuffix: `/integrations/dlp/rules?orgId=${await this.orgStore.orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ rule }),
    });
    this.reload();
  }

  async editRule(rule?: DLPRule) {
    if (!rule) {
      return;
    }
    const newRule = await modalService.openModal(DLPRuleModal, { rule }).promise;
    if (!newRule) return;
    await putJSON({
      urlSuffix: `/integrations/dlp/rules/${rule.id}?orgId=${await this.orgStore.orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ rule: newRule }),
    });
    this.reload();
  }

  async importConfigs() {
    const res = await modalService.openModal(ImportConfigModal).promise;
    if (!res?.value) return;
    modalService.openModal(Toast, { text: i18n.t("super_admin_org_configs.importing_configuration", { orgName: res.label }) });
    try {
      await postJSON({
        urlSuffix: `/integrations/dlp/copy-policies-rules`,
        etoken: this.userStore.etoken ?? "",
        pod: await this.orgStore.wPod,
        body: JSON.stringify({ sourceOrgId: res.value, targetOrgId: await this.orgStore.orgId }),
      });
      this.reload();
      modalService.openModal(Toast, { text: i18n.t("super_admin_org_configs.import_configuration_success") });
    } catch {
      modalService.openModal(Toast, { text: i18n.t("super_admin_org_configs.import_configuration_failed"), type: "error" });
    }
  }
}
